import {
    DateObjectUnits,
    DateTime,
    DateTimeFormatOptions,
    DateTimeUnit,
    Duration,
    DurationLikeObject,
    LocaleOptions,
} from 'luxon';

const PARSE_FORMATS = ['yyyy-MM-ddTHH:mm:ss.SSS', 'yyyy-MM-ddTHH:mm:ss', 'yyyy-MM-ddTHH:mm', 'yyyy-MM-dd'];

const BROWSER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

export default class LocalTime {
    private static IANA_TIMEZONE: string | undefined;

    public static timeZoneOffset(): number {
        return LocalTime.now().timeZoneOffset();
    }

    public static set IanaTimeZone(ianaTimeZone: string | undefined) {
        LocalTime.IANA_TIMEZONE = ianaTimeZone;
    }

    // Construction operations

    private readonly localTime = DateTime.now();

    constructor(localTime: DateTime) {
        this.localTime = localTime;
    }

    public static now(): LocalTime {
        const now = DateTime.fromJSDate(new Date(), { zone: LocalTime.IANA_TIMEZONE });
        return new LocalTime(now);
    }

    public static fromServer(utcDateTime: string | null): LocalTime | null {
        if (!utcDateTime) return null;
        const hasTimeZoneOffset = /Z$|[+-]\d+(:\d+)?$/.test(utcDateTime);
        const localTime = hasTimeZoneOffset
            ? DateTime.fromISO(utcDateTime).setZone(LocalTime.IANA_TIMEZONE)
            : DateTime.fromISO(utcDateTime, { zone: 'UTC' }).setZone(LocalTime.IANA_TIMEZONE);

        return new LocalTime(localTime);
    }

    public static fromMillis(millisSinceEpoch: number): LocalTime {
        const localTime = LocalTime.convertToLocalTime(millisSinceEpoch);
        return localTime && new LocalTime(localTime);
    }

    public static fromBrowser(dateTime: string | number | Date): LocalTime {
        const localTime = LocalTime.convertToLocalTime(dateTime);
        return localTime && new LocalTime(localTime);
    }

    public static fromISOString(isoString: string): LocalTime {
        const localTime = LocalTime.convertToLocalTime(isoString);
        return localTime && new LocalTime(localTime);
    }

    public clone(): LocalTime {
        const clone = DateTime.fromMillis(this.localTime.toMillis(), { zone: LocalTime.IANA_TIMEZONE });
        return new LocalTime(clone);
    }

    // Manipulation operations
    public set = (values: DateObjectUnits): LocalTime => new LocalTime(this.localTime.set(values));
    public startOf = (unit: DateTimeUnit): LocalTime => new LocalTime(this.localTime.startOf(unit));

    public plus = (duration: Duration | number | DurationLikeObject): LocalTime =>
        new LocalTime(this.localTime.plus(duration));
    public minus = (duration: Duration | number | DurationLikeObject): LocalTime =>
        new LocalTime(this.localTime.minus(duration));

    // Parts
    public get(unit: keyof DateTime) {
        return this.localTime.get(unit);
    }

    // Comparison operations
    public isSame(dateTime: LocalTime): boolean {
        return this.localTime.equals(dateTime.localTime);
    }

    public diff = (dateTime: LocalTime) => this.localTime.diff(dateTime.localTime);

    public isBefore = (dateTime: LocalTime) => this.localTime.valueOf() < dateTime.valueOf();
    public isSameOrBefore = (dateTime: LocalTime) => this.localTime.valueOf() <= dateTime.valueOf();
    public isAfter = (dateTime: LocalTime) => this.localTime.valueOf() > dateTime.valueOf();
    public isSameOrAfter = (dateTime: LocalTime) => this.localTime.valueOf() >= dateTime.valueOf();

    public static max(...dateTimes: LocalTime[]): LocalTime | null {
        if (!(dateTimes && dateTimes.length)) {
            return null;
        }

        return dateTimes.reduce((max, facilityDateTime) => {
            if (!max) return facilityDateTime;
            if (!facilityDateTime) return max;

            return !max || facilityDateTime.isAfter(max) ? facilityDateTime : max;
        });
    }

    public static min(...dateTimes: LocalTime[]): LocalTime | null {
        if (!(dateTimes && dateTimes.length)) {
            return null;
        }

        return dateTimes.reduce((min, facilityDateTime) => {
            if (!min) return facilityDateTime;
            if (!facilityDateTime) return min;

            return facilityDateTime.isBefore(min) ? facilityDateTime : min;
        });
    }

    /**
     * Returns the underlying Luxon DateTime instance, set to the user's timezone. e.g. if the LocalTime is
     * 8AM UTC+8, and the user is in Brisbane, this will return 10AM UTC+10.
     */
    public toUserTime = () => this.localTime.setZone(BROWSER_TIMEZONE);

    /**
     * Returns the underlying Luxon DateTime instance
     */
    public toLocaleTime = () => this.localTime;

    /**
     * Returns the underlying Luxon DateTime instance, set to the UTC.
     */
    public toUtcTime = () => this.toUserTime().setZone('UTC');

    // Output operations
    public valueOf = () => this.localTime.valueOf();

    /*
     * Returns Luxon DateTime a string representing Local/Browser Time..
     */
    public toUserString = (options?: LocaleOptions & DateTimeFormatOptions) =>
        this.toUserTime().toLocaleString(options);

    /*
     * Returns the Luxon DateTime as string representing Facility Time (Locale not local).
     */
    public toLocaleString = (options?: LocaleOptions & DateTimeFormatOptions) => this.localTime.toLocaleString(options);

    /*
     * Returns Luxon DateTime as a string representing UTC Time.
     */
    public toUtcString = (options?: LocaleOptions & DateTimeFormatOptions) =>
        this.toUserTime().setZone('UTC').toLocaleString(options);

    /*
     * Format this local time as an ISO string.
     */
    public toISO = () => {
        // There's a weird bug with some timezones (e.g. America/Bogata) where date/times before the UNIX epoch don't
        // convert properly. For these date times, we need to convert the timezone back to UTC before outputting the
        // ISO string.
        return this.localTime.valueOf() < 0 ? this.localTime.setZone('UTC').toISO() : this.localTime.toISO();
    };

    /** Returns the time as a JS date. */
    public toDate = () => this.localTime.toJSDate();

    /*
     * Returns the time as a JS date, preserving the time of day in the source time zone.
     */
    public toBrowser = () => {
        return this.localTime.setZone(BROWSER_TIMEZONE, { keepLocalTime: true }).toJSDate();
    };

    public toString = () => {
        return this.localTime.toLocaleString(DateTime.DATETIME_MED);
    };

    /*
     * Returns the UTC offset of the DateTime in minutes.
     */
    public timeZoneOffset = () => this.localTime.offset;
    public timeZoneOffsetNameLong = () => this.localTime.offsetNameLong;
    public timeZoneOffsetNameShort = () => this.localTime.offsetNameShort;

    /**
     * Gets the date and time string from the given time object, ignoring any timezone information.
     *
     * @param {number | string | Date | DateTime} time The time object to convert
     * @returns {string} Time string mostly compliant with ISO8601, but without any timezone information.
     */
    private static convertToLocalTime(time: number | string | Date | DateTime) {
        if (DateTime.isDateTime(time)) {
            return time.setZone(LocalTime.IANA_TIMEZONE);
        }

        if (typeof time === 'number') {
            return DateTime.fromMillis(time, { zone: LocalTime.IANA_TIMEZONE });
        }

        if (time instanceof Date) {
            const browserTime = DateTime.fromJSDate(time);
            return browserTime.setZone(LocalTime.IANA_TIMEZONE, { keepLocalTime: true });
        }

        if (typeof time === 'string') {
            const utcTime = DateTime.fromISO(time, { zone: LocalTime.IANA_TIMEZONE });
            if (utcTime.isValid) return utcTime;

            for (const formatString in PARSE_FORMATS) {
                const localTime = DateTime.fromFormat(time, formatString, { zone: LocalTime.IANA_TIMEZONE });
                if (localTime.isValid) return localTime;
            }

            throw new Error(`Could not parse time string: ${time}`);
        }

        throw new Error(`Unrecognized time: ${time}`);
    }
}

export interface ITimeRange {
    start: LocalTime | null;
    end: LocalTime | null;
}

export interface ITimeRangeMillis {
    startMillis: number;
    endMillis: number;
}
